import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Card } from '@mypoz/ui';

import { actionLinkClass } from '@/lib/styles.js';
import { ORDERS_ROUTE } from '@/shell/navigation.js';
import { useAccountProfile } from '../context.js';
import { VERIFICATION_GATE_ENABLED } from '../verification.js';
import { VerificationNotice } from './VerificationNotice.js';

/**
 * Create order, only for a verified customer (MYP-142).
 *
 * The vendor creates orders only for customers whose identity MyPoz staff have verified. Without
 * this an unverified customer fills in all four steps and is refused at the end. So the route waits
 * here, inside `SessionGate` (the account is loaded by then), and shows why and how to fix it
 * (`VerificationNotice`).
 *
 * Once "check again" says verified the order form appears in place, with any packages chosen on
 * the Packages page still chosen (they travel in the route's state, which this screen leaves alone).
 */
export function VerificationGate({ children }: { children: ReactNode }) {
  const { profile } = useAccountProfile();
  if (!VERIFICATION_GATE_ENABLED || profile.verification === 'verified') return <>{children}</>;
  return <NotVerified />;
}

function NotVerified() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <VerificationNotice
          title={t('account.verification.gate.title')}
          body={t('account.verification.gate.body')}
          finalStep={t('account.verification.gate.step3')}
        >
          <Link to={ORDERS_ROUTE} className={actionLinkClass('justify-self-start')}>
            {t('account.verification.gate.backToOrders')}
          </Link>
        </VerificationNotice>
      </Card>
    </div>
  );
}
